import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Grid,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Download as DownloadIcon } from "@mui/icons-material";
import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://printformee.com/api";

const emptyAdminSummary = {
  totalRevenue: 0,
  totalCommission: 0,
  totalTax: 0,
  totalRefunds: 0,
  netEarnings: 0,
  totalOrders: 0,
  averageOrderValue: 0,
};

const EarningReports = () => {
  console.log("EarningReports component is rendering!");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [activeTab, setActiveTab] = useState(0);
  const [timeFilter, setTimeFilter] = useState("thisMonth");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [adminSummaryData, setAdminSummaryData] = useState(emptyAdminSummary);
  const [adminEarningsData, setAdminEarningsData] = useState([]);
  const [paymentStatusData, setPaymentStatusData] = useState([]);
  const [totalSalesData, setTotalSalesData] = useState([]);

  const fetchAdminSummaryData = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/admin-earnings/summary`,
        {
          params: { timeFilter },
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setAdminSummaryData(response.data || emptyAdminSummary);
    } catch (error) {
      console.error("Error fetching admin summary:", error);
      setAdminSummaryData(emptyAdminSummary);
    }
  };

  const fetchAdminEarningsChart = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/admin-earnings/chart`, {
        params: { timeFilter },
        headers: { Authorization: `Bearer ${token}` },
      });
      setAdminEarningsData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching admin earnings chart:", error);
      setAdminEarningsData([]);
    }
  };

  const fetchAdminPaymentStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/admin-earnings/payment-status`,
        {
          params: { timeFilter },
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setPaymentStatusData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching payment status:", error);
      setPaymentStatusData([]);
    }
  };

  const fetchAdminTotalSales = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/admin-earnings/total-sales`,
        {
          params: { timeFilter },
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setTotalSalesData(
        Array.isArray(response.data?.sales) ? response.data.sales : [],
      );
    } catch (error) {
      console.error("Error fetching total sales:", error);
      setTotalSalesData([]);
    }
  };

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError("");
      try {
        console.log("Loading earning reports with API:", API_BASE_URL);
        if (activeTab === 0) {
          await Promise.all([
            fetchAdminSummaryData(),
            fetchAdminEarningsChart(),
            fetchAdminPaymentStatus(),
            fetchAdminTotalSales(),
          ]);
        }
      } catch (err) {
        console.error("Error loading earning reports:", err);
        setError(
          "Unable to load earning report data. Please check if the backend server is running.",
        );
        setAdminEarningsData([]);
        setPaymentStatusData([]);
        setAdminSummaryData(emptyAdminSummary);
        setTotalSalesData([]);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [activeTab, timeFilter]);

  const handleExport = () => {
    console.log(
      "Export data for:",
      timeFilter,
      activeTab === 0 ? "admin" : "admin",
    );
  };

  const LineChart = ({ data, title, isMobile, isSmallMobile }) => {
    const items = Array.isArray(data) ? data : [];
    const chartHeight = isSmallMobile ? 180 : isMobile ? 200 : 240;
    const barWidth = isSmallMobile ? 20 : 30;

    return (
      <Card sx={{ height: "100%" }}>
        <CardContent sx={{ p: isSmallMobile ? 2 : 3 }}>
          <Typography
            variant={isSmallMobile ? "h6" : "h6"}
            sx={{ mb: 2, fontWeight: 600 }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              height: chartHeight,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: isSmallMobile ? 1 : 2,
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              backgroundColor: "#fafafa",
              overflowX: "auto",
            }}
          >
            {!Array.isArray(data) || data.length === 0 ? (
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  No chart data available
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Earning statistics will appear here when data is loaded
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: isSmallMobile ? "flex-start" : "space-around",
                  width: "100%",
                  height: "100%",
                  gap: isSmallMobile ? 1 : 0.5,
                  minWidth: isSmallMobile ? items.length * 50 : "auto",
                }}
              >
                {items.map((item, index) => {
                  const earnings = Number(item.earnings || item.amount || 0);
                  const maxEarning = Math.max(
                    1,
                    ...items.map((d) => Number(d.earnings || d.amount || 0)),
                  );
                  const height =
                    maxEarning > 0
                      ? (earnings / maxEarning) * (chartHeight - 60)
                      : 0;

                  return (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        flex: isSmallMobile ? "0 0 auto" : 1,
                        maxWidth: isSmallMobile ? 40 : 60,
                        minWidth: isSmallMobile ? 35 : "auto",
                      }}
                    >
                      <Box
                        sx={{
                          width: isSmallMobile ? 6 : 8,
                          height: Math.max(height, 2),
                          backgroundColor: "#1976d2",
                          borderRadius: 1,
                          mb: 1,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            backgroundColor: "#1565c0",
                            transform: "scaleY(1.05)",
                          },
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: isSmallMobile ? "0.65rem" : "0.7rem",
                          textAlign: "center",
                          wordBreak: "break-word",
                          whiteSpace: "nowrap",
                          fontWeight: 500,
                        }}
                      >
                        {item.month || item.period || `P${index + 1}`}
                      </Typography>
                      {earnings > 0 && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: isSmallMobile ? "0.6rem" : "0.65rem",
                            color: "#666",
                            textAlign: "center",
                            mt: 0.25,
                          }}
                        >
                          Rs{earnings.toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const DonutChart = ({ data, title, isMobile, isSmallMobile }) => {
    const items = Array.isArray(data) ? data : [];
    const chartSize = isSmallMobile ? 80 : isMobile ? 100 : 120;
    const innerSize = chartSize / 2;

    return (
      <Card sx={{ height: "100%" }}>
        <CardContent sx={{ p: isSmallMobile ? 2 : 3 }}>
          <Typography
            variant={isSmallMobile ? "h6" : "h6"}
            sx={{ mb: 2, fontWeight: 600 }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-around",
              alignItems: "center",
              p: isSmallMobile ? 1 : 2,
              flexDirection: isSmallMobile ? "column" : "row",
              gap: isSmallMobile ? 2 : 0,
            }}
          >
            <Box
              sx={{ position: "relative", width: chartSize, height: chartSize }}
            >
              <Box
                sx={{
                  width: chartSize,
                  height: chartSize,
                  borderRadius: "50%",
                  background: (() => {
                    if (items.length === 0) return "#e0e0e0";
                    let cumulative = 0;
                    const segments = items.map((item) => {
                      const start = cumulative;
                      cumulative += Number(item.value || 0) * 3.6;
                      return `${item.color} ${start}deg ${cumulative}deg`;
                    });
                    return `conic-gradient(${segments.join(", ")})`;
                  })(),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  transition: "transform 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.05)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: innerSize,
                    height: innerSize,
                    borderRadius: "50%",
                    backgroundColor: "white",
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ flex: 1 }}>
              {items.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: isSmallMobile ? 0.5 : 1,
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: isSmallMobile ? 10 : 12,
                      height: isSmallMobile ? 10 : 12,
                      backgroundColor: item.color,
                      borderRadius: "50%",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant={isSmallMobile ? "caption" : "body2"}
                    sx={{ fontSize: isSmallMobile ? "0.7rem" : "0.875rem" }}
                  >
                    {item.name}: {item.value}%
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box
      sx={{
        p: isSmallMobile ? 2 : isMobile ? 2.5 : 3,
        backgroundColor: "#f7f8fc",
        minHeight: "100vh",
      }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography
          variant={isSmallMobile ? "h5" : isMobile ? "h4" : "h3"}
          sx={{
            fontWeight: 600,
            color: "#1a1a1a",
            textAlign: isSmallMobile ? "center" : "left",
          }}
        >
          Earnings Reports
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: isSmallMobile ? 2 : 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexDirection: isSmallMobile ? "column" : "row",
              gap: isSmallMobile ? 2 : 0,
            }}
          >
            <FormControl
              size="small"
              sx={{
                minWidth: isSmallMobile ? "100%" : 200,
                flex: isSmallMobile ? 1 : "auto",
              }}
            >
              <Select
                value={timeFilter}
                onChange={(e) => setTimeFilter(e.target.value)}
                displayEmpty
              >
                <MenuItem value="thisYear">This Year</MenuItem>
                <MenuItem value="thisMonth">This Month</MenuItem>
                <MenuItem value="thisWeek">This Week</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="lastYear">Last Year</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              size={isSmallMobile ? "small" : "medium"}
              sx={{
                px: isSmallMobile ? 2 : 3,
                py: isSmallMobile ? 1 : 1.5,
              }}
            >
              {isSmallMobile ? "Export" : "Export Data"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(event, value) => setActiveTab(value)}
          variant={isSmallMobile ? "scrollable" : "standard"}
          scrollButtons={isSmallMobile ? "auto" : false}
          allowScrollButtonsMobile
          sx={{
            "& .MuiTab-root": {
              fontSize: isSmallMobile ? "0.75rem" : "0.875rem",
              minWidth: isSmallMobile ? "auto" : 120,
              px: isSmallMobile ? 1 : 2,
            },
          }}
        >
          <Tab label={isSmallMobile ? "Admin" : "Admin Earning"} />
        </Tabs>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 400,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {activeTab === 0 && (
            <Box>
              <Grid container spacing={isSmallMobile ? 2 : 3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={8} lg={7}>
                  <LineChart
                    data={adminEarningsData}
                    title="Earning Statistics"
                    isMobile={isMobile}
                    isSmallMobile={isSmallMobile}
                  />
                </Grid>
                <Grid item xs={12} md={4} lg={5}>
                  <DonutChart
                    data={paymentStatusData}
                    title="Payment Status"
                    isMobile={isMobile}
                    isSmallMobile={isSmallMobile}
                  />
                </Grid>
              </Grid>

              <Card sx={{ mb: 3 }}>
                <CardContent sx={{ p: isSmallMobile ? 2 : 3 }}>
                  <Grid container spacing={isSmallMobile ? 2 : 3}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box
                        sx={{ textAlign: isSmallMobile ? "center" : "left" }}
                      >
                        <Typography
                          variant={isSmallMobile ? "caption" : "body2"}
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Total Revenue
                        </Typography>
                        <Typography
                          variant={isSmallMobile ? "h6" : "h5"}
                          sx={{ fontWeight: 600 }}
                        >
                          Rs{" "}
                          {Number(
                            adminSummaryData.totalRevenue || 0,
                          ).toLocaleString()}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box
                        sx={{ textAlign: isSmallMobile ? "center" : "left" }}
                      >
                        <Typography
                          variant={isSmallMobile ? "caption" : "body2"}
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Commission
                        </Typography>
                        <Typography
                          variant={isSmallMobile ? "h6" : "h5"}
                          sx={{ fontWeight: 600 }}
                        >
                          Rs{" "}
                          {Number(
                            adminSummaryData.totalCommission || 0,
                          ).toLocaleString()}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box
                        sx={{ textAlign: isSmallMobile ? "center" : "left" }}
                      >
                        <Typography
                          variant={isSmallMobile ? "caption" : "body2"}
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Refunds
                        </Typography>
                        <Typography
                          variant={isSmallMobile ? "h6" : "h5"}
                          sx={{ fontWeight: 600 }}
                        >
                          Rs{" "}
                          {Number(
                            adminSummaryData.totalRefunds || 0,
                          ).toLocaleString()}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box
                        sx={{ textAlign: isSmallMobile ? "center" : "left" }}
                      >
                        <Typography
                          variant={isSmallMobile ? "caption" : "body2"}
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Net Earnings
                        </Typography>
                        <Typography
                          variant={isSmallMobile ? "h6" : "h5"}
                          sx={{ fontWeight: 600, color: "success.main" }}
                        >
                          Rs{" "}
                          {Number(
                            adminSummaryData.netEarnings || 0,
                          ).toLocaleString()}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card>
                <CardContent sx={{ p: isSmallMobile ? 2 : 3 }}>
                  <Typography
                    variant={isSmallMobile ? "h6" : "h6"}
                    sx={{ mb: 2, fontWeight: 600 }}
                  >
                    Total Sales
                  </Typography>
                  {isMobile ? (
                    // Mobile Card View
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {totalSalesData.length > 0 ? (
                        totalSalesData.map((row) => (
                          <Card
                            key={row.id}
                            sx={{
                              p: 2,
                              border: "1px solid #e0e0e0",
                              backgroundColor: "#fafafa",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                backgroundColor: "#f0f0f0",
                                transform: "translateY(-2px)",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 1.5,
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: isSmallMobile
                                    ? "0.8rem"
                                    : "0.875rem",
                                  color: "#1976d2",
                                }}
                              >
                                {row.orderId}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  fontSize: isSmallMobile
                                    ? "0.65rem"
                                    : "0.75rem",
                                }}
                              >
                                {row.date}
                              </Typography>
                            </Box>

                            <Typography
                              variant="body2"
                              sx={{
                                mb: 1.5,
                                fontSize: isSmallMobile ? "0.8rem" : "0.875rem",
                                fontWeight: 500,
                              }}
                            >
                              {row.customerName}
                            </Typography>

                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    fontSize: isSmallMobile
                                      ? "0.65rem"
                                      : "0.75rem",
                                  }}
                                >
                                  Total:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: isSmallMobile
                                      ? "0.8rem"
                                      : "0.875rem",
                                  }}
                                >
                                  Rs{" "}
                                  {Number(
                                    row.totalAmount || 0,
                                  ).toLocaleString()}
                                </Typography>
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    fontSize: isSmallMobile
                                      ? "0.65rem"
                                      : "0.75rem",
                                  }}
                                >
                                  Commission:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 500,
                                    fontSize: isSmallMobile
                                      ? "0.8rem"
                                      : "0.875rem",
                                    color: "#388e3c",
                                  }}
                                >
                                  Rs{" "}
                                  {Number(row.commission || 0).toLocaleString()}
                                </Typography>
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    fontSize: isSmallMobile
                                      ? "0.65rem"
                                      : "0.75rem",
                                  }}
                                >
                                  Net Earning:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: isSmallMobile
                                      ? "0.8rem"
                                      : "0.875rem",
                                    color: "#0d47a1",
                                  }}
                                >
                                  Rs{" "}
                                  {Number(row.netEarning || 0).toLocaleString()}
                                </Typography>
                              </Box>
                            </Box>
                          </Card>
                        ))
                      ) : (
                        <Box sx={{ textAlign: "center", py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No sales data available
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    // Desktop Table View
                    <TableContainer
                      component={Paper}
                      sx={{
                        overflowX: "auto",
                        borderRadius: 2,
                        "&::-webkit-scrollbar": {
                          height: 8,
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#f1f1f1",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#888",
                          borderRadius: 4,
                        },
                      }}
                    >
                      <Table sx={{ minWidth: 800 }}>
                        <TableHead sx={{ backgroundColor: "#f8f9fa" }}>
                          <TableRow>
                            <TableCell sx={{ minWidth: 120, fontWeight: 600 }}>
                              Date
                            </TableCell>
                            <TableCell sx={{ minWidth: 150, fontWeight: 600 }}>
                              Order ID
                            </TableCell>
                            <TableCell sx={{ minWidth: 180, fontWeight: 600 }}>
                              Customer Name
                            </TableCell>
                            <TableCell
                              sx={{ minWidth: 120, fontWeight: 600 }}
                              align="right"
                            >
                              Total Amount
                            </TableCell>
                            <TableCell
                              sx={{ minWidth: 120, fontWeight: 600 }}
                              align="right"
                            >
                              Commission
                            </TableCell>
                            <TableCell
                              sx={{ minWidth: 100, fontWeight: 600 }}
                              align="right"
                            >
                              VAT/TAX
                            </TableCell>
                            <TableCell
                              sx={{ minWidth: 120, fontWeight: 600 }}
                              align="right"
                            >
                              Refund Given
                            </TableCell>
                            <TableCell
                              sx={{ minWidth: 120, fontWeight: 600 }}
                              align="right"
                            >
                              Net Earning
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {totalSalesData.length > 0 ? (
                            totalSalesData.map((row) => (
                              <TableRow
                                key={row.id}
                                sx={{
                                  backgroundColor:
                                    row.id % 2 === 0 ? "white" : "#f8f9fa",
                                  "&:hover": {
                                    backgroundColor: "#e3f2fd",
                                  },
                                }}
                              >
                                <TableCell sx={{ minWidth: 120 }}>
                                  {row.date}
                                </TableCell>
                                <TableCell
                                  sx={{ minWidth: 150, fontWeight: 500 }}
                                >
                                  {row.orderId}
                                </TableCell>
                                <TableCell sx={{ minWidth: 180 }}>
                                  {row.customerName}
                                </TableCell>
                                <TableCell sx={{ minWidth: 120 }} align="right">
                                  Rs{" "}
                                  {Number(
                                    row.totalAmount || 0,
                                  ).toLocaleString()}
                                </TableCell>
                                <TableCell
                                  sx={{ minWidth: 120, color: "#388e3c" }}
                                  align="right"
                                >
                                  Rs{" "}
                                  {Number(row.commission || 0).toLocaleString()}
                                </TableCell>
                                <TableCell sx={{ minWidth: 100 }} align="right">
                                  Rs {Number(row.vatTax || 0).toLocaleString()}
                                </TableCell>
                                <TableCell sx={{ minWidth: 120 }} align="right">
                                  Rs {Number(row.refundGiven || 0).toFixed(2)}
                                </TableCell>
                                <TableCell sx={{ minWidth: 120 }} align="right">
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 600,
                                      color: "success.main",
                                    }}
                                  >
                                    Rs {Number(row.netEarning || 0).toFixed(2)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={8} align="center">
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  No sales data available
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default EarningReports;
